import { useId, type HTMLAttributes, type ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import { IconReset } from "./icons";
import { SettingOrigin } from "./SettingOrigin";
import {
  canResetSetting,
  settingOriginKind,
  type SettingLevel,
  type SettingOriginValue,
} from "./settingOriginModel";
import { Spinner } from "./Spinner";
import "./SettingRow.css";

export type SettingRowVariant = "single" | "bulk" | "readonly";

export interface SettingRowIds {
  /** id of the visible label — use as the control's aria-labelledby. */
  labelId: string;
  /** id of the help text (if any). */
  helpId: string | undefined;
  /** Value for the control's aria-describedby (help + origin badge). */
  describedBy: string | undefined;
}

type ControlSlot = ReactNode | ((ids: SettingRowIds) => ReactNode);

export interface SettingRowProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  label: ReactNode;
  help?: ReactNode;
  /** Settings-spec key; shown in mono under the label and used as data-setting-key. */
  settingKey?: string;
  /** The editor. A function receives ids for aria wiring; `control` is an alias. */
  children?: ControlSlot;
  control?: ControlSlot;
  /** Origin of the current value; "mixed" in bulk editors when the selection disagrees. */
  origin?: SettingOriginValue;
  /** Level being edited (decides wording + whether reset shows). Default "mapping". */
  level?: SettingLevel;
  /** @deprecated alias of `level` (early API card); `level` wins when both are set. */
  viewLevel?: SettingLevel;
  variant?: SettingRowVariant;
  /** Reset to inherited. Rendered only when origin is set at `level` (and not readonly). */
  onReset?: () => void;
  /** Tooltip detail for reset, e.g. "on (global)" → "Resets to: on (global)". */
  inheritedValueLabel?: string;
  /** Accessible name of the reset button. Default "Reset to inherited". */
  resetLabel?: string;
  /** Muted label; pass `disabled` to the control too. */
  disabled?: boolean;
  /** Shows an inline spinner (fades in after 300 ms so fast saves do not flicker). */
  saving?: boolean;
  /** If the control is a native labelable element with this id, the label targets it. */
  htmlFor?: string;
}

/**
 * One delivery setting: label, help, origin badge, control and "Reset to inherited".
 * role="group" labelled by the label and described by help + origin badge.
 */
export function SettingRow({
  label,
  help,
  settingKey,
  children,
  control,
  origin,
  level: levelProp,
  viewLevel,
  variant = "single",
  onReset,
  inheritedValueLabel,
  resetLabel = "Reset to inherited",
  disabled = false,
  saving = false,
  htmlFor,
  className,
  ...rest
}: SettingRowProps) {
  const level: SettingLevel = levelProp ?? viewLevel ?? "mapping";
  const auto = useId();
  const base = `sr${auto}`;
  const labelId = `${base}-label`;
  const helpId = help ? `${base}-help` : undefined;
  const originId = origin ? `${base}-origin` : undefined;
  const describedBy = [helpId, originId].filter(Boolean).join(" ") || undefined;
  const ids: SettingRowIds = { labelId, helpId, describedBy };

  const slot = control ?? children;
  const rendered = typeof slot === "function" ? slot(ids) : slot;
  const kind = origin ? settingOriginKind(origin, level) : undefined;
  const showReset =
    variant !== "readonly" && !!onReset && !!origin && canResetSetting(origin, level);

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      aria-busy={saving || undefined}
      data-setting-key={settingKey}
      className={cx(
        "ui-setting-row",
        `ui-setting-row--${variant}`,
        kind === "overridden" && "ui-setting-row--overridden",
        disabled && "ui-setting-row--disabled",
        className,
      )}
      {...rest}
    >
      <div className="ui-setting-row__meta">
        <div className="ui-setting-row__head">
          {htmlFor ? (
            <label className="ui-setting-row__label" id={labelId} htmlFor={htmlFor}>
              {label}
            </label>
          ) : (
            <span className="ui-setting-row__label" id={labelId}>
              {label}
            </span>
          )}
          {origin ? <SettingOrigin id={originId} origin={origin} level={level} /> : null}
        </div>
        {settingKey ? <code className="ui-setting-row__key">{settingKey}</code> : null}
        {help ? (
          <p className="ui-setting-row__help" id={helpId}>
            {help}
          </p>
        ) : null}
      </div>
      <div className="ui-setting-row__control">
        {saving ? (
          <span className="ui-setting-row__saving">
            <Spinner size="sm" label="Saving" />
          </span>
        ) : null}
        {rendered}
        <span className="ui-setting-row__reset">
          {showReset ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<IconReset />}
              aria-label={resetLabel}
              title={inheritedValueLabel ? `Resets to: ${inheritedValueLabel}` : resetLabel}
              disabled={disabled || saving}
              onClick={onReset}
            >
              Reset
            </Button>
          ) : null}
        </span>
      </div>
    </div>
  );
}
